// URL <-> filter-state plumbing for the Ground Tour listing (/web/ground-tour).
// Mirrors utils/listingSearchParams for the series listing, but carries the
// ground-specific facets: tour type, guided language, inclusions, promotions,
// trip duration and budget per traveller.
//
// Facet vocabularies (languages, inclusions, promotions, max duration) are owned
// by the backend and arrive in meta.filters; only the two tour types are named
// here, because the URL keeps short readable values rather than the enum keys.

import type { LandTourSearchParams } from "../api-services/land-tours-api.service";

export type GroundSortValue = "relevance" | "price_asc" | "price_desc";

export type GroundTourType = "private" | "share";

// The listing URL uses "private"/"share"; the API's category enum uses
// "private_tour"/"seat_in_coach". Mapped at the API boundary so the nav entry
// points and shareable URLs stay readable.
export const TOUR_TYPE_CATEGORY: Record<GroundTourType, string> = {
  private: "private_tour",
  share: "seat_in_coach",
};

// The design calls seat-in-coach "Share Tour"; the API titleizes the enum to
// "Seat In Coach". Display wins.
export const TOUR_TYPE_LABEL: Record<GroundTourType, string> = {
  private: "Private Tour",
  share: "Share Tour",
};

export const categoryToTourType = (category?: string): GroundTourType =>
  category === "Seat In Coach" || category === "seat_in_coach" ? "share" : "private";

// Trip Duration slider: 0 means any length, as in the B2B listing. The ceiling
// comes from meta.filters.max_duration.
export const DURATION_ANY = 0;
export const DURATION_FALLBACK_MAX = 14;
export const POPULAR_DURATIONS = [4, 5, 6, 8];

// Budget per traveller range, as shown in the Minimum/Maximum inputs.
export const BUDGET_MIN = 1;
export const BUDGET_MAX = 30000;
export const BUDGET_STEP = 100;

export interface GroundFilterState {
  keyword: string;
  /** Travel month as YYYY-MM-01; the API widens it to the whole month. */
  month: string | null;
  tourTypes: GroundTourType[];
  languages: string[];
  inclusions: string[];
  promotions: string[];
  /** Exact trip length, set by the slider or its preset chips. */
  duration: number | null;
  budget: [number, number] | null;
  sort: GroundSortValue;
}

const splitList = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const isSortValue = (value: string | null): value is GroundSortValue =>
  value === "relevance" || value === "price_asc" || value === "price_desc";

const toNumber = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseGroundParams = (searchParams: URLSearchParams): GroundFilterState => {
  const sort = searchParams.get("sort");
  const budgetMin = toNumber(searchParams.get("budget_min"));
  const budgetMax = toNumber(searchParams.get("budget_max"));

  // The two nav entry points differ only by `?tour_types=private|share`, so the
  // entry-point facet is just another filter param.
  const tourTypes = splitList(searchParams.get("tour_types")) as GroundTourType[];

  return {
    keyword: searchParams.get("keyword") || "",
    month: searchParams.get("month"),
    tourTypes,
    languages: splitList(searchParams.get("languages")),
    inclusions: splitList(searchParams.get("inclusions")),
    promotions: splitList(searchParams.get("promotions")),
    duration: toNumber(searchParams.get("duration")),
    budget: budgetMin !== null && budgetMax !== null ? [budgetMin, budgetMax] : null,
    sort: isSortValue(sort) ? sort : "relevance",
  };
};

export const toGroundQueryString = (state: GroundFilterState): string => {
  const params = new URLSearchParams();

  if (state.keyword) params.set("keyword", state.keyword);
  if (state.month) params.set("month", state.month);
  if (state.tourTypes.length) params.set("tour_types", state.tourTypes.join(","));
  if (state.languages.length) params.set("languages", state.languages.join(","));
  if (state.inclusions.length) params.set("inclusions", state.inclusions.join(","));
  if (state.promotions.length) params.set("promotions", state.promotions.join(","));
  if (state.duration) params.set("duration", String(state.duration));
  if (state.budget) {
    params.set("budget_min", String(state.budget[0]));
    params.set("budget_max", String(state.budget[1]));
  }
  if (state.sort !== "relevance") params.set("sort", state.sort);

  return params.toString();
};

const SORT_PARAM: Record<GroundSortValue, LandTourSearchParams["sortBy"]> = {
  relevance: undefined,
  price_asc: "lowest_price",
  price_desc: "highest_price",
};

export const toLandTourParams = (
  state: GroundFilterState,
  page: number,
  perPage: number
): LandTourSearchParams => {
  const params: LandTourSearchParams = { page, per: perPage };

  if (state.keyword) params.keyword = state.keyword;
  if (state.month) params.month = state.month;
  if (state.tourTypes.length) params.category = state.tourTypes.map((type) => TOUR_TYPE_CATEGORY[type]);
  if (state.languages.length) params.guide_languages = state.languages;
  if (state.inclusions.length) params.inclusions = state.inclusions;
  if (state.promotions.length) params.tag_ids = state.promotions;
  if (state.duration) params.duration = state.duration;
  if (state.budget) {
    params.min_price = state.budget[0];
    params.max_price = state.budget[1];
  }

  const sortBy = SORT_PARAM[state.sort];
  if (sortBy) params.sortBy = sortBy;

  return params;
};

// The Filters badge reports how many filters are applied, counting every facet
// the dialog can set - including the ones that also surface as chips on the bar.
// The design's Result frame shows "Filters 1" next to a single selected
// promotion chip, so a bar chip is counted rather than excluded.
export const groundFilterCount = (state: GroundFilterState): number =>
  state.tourTypes.length +
  state.languages.length +
  state.inclusions.length +
  state.promotions.length +
  (state.duration ? 1 : 0) +
  (state.budget ? 1 : 0);

export const toggleValue = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
